import h from 'hyperscript';
import btnIcon from '@disy/cadenza-icons/table-settings.svg';
import dragIcon from '@disy/cadenza-icons/drag-indicator.svg';
import clearRoundIcon from '@disy/cadenza-icons/clear-round.svg';
import Sortable from 'sortablejs';

import {
  VIRTUAL_VALUES_DIMENSION,
  isColsCountMinValid,
  isMeasuresCountMinValid,
  isRowsCountMinValid,
  isValidPivotConfiguration
} from 'cadenza/result-table/pivot-table-features/pivot-table-features';

import { AFTER_LOAD_DATA } from 'ui/data-loading-event-types';
import 'ui/switch/switch';
import { setupMenuButton } from 'ui/menu/menu';

import { supportsTouch } from 'cadenza/utils/media';
import { PARAMETER_PIVOT_CONFIG } from 'cadenza/api-client/api-constants';
import { hasParameter } from 'cadenza/utils/url-param-utils';
import { on, unByKey } from 'cadenza/utils/event-util';
import { icon } from 'cadenza/utils/icon/icon';
import {
  COLUMN_ORDER_CHANGE,
  COLUMN_VISIBILITY_CHANGE,
  PIVOT_CHANGE,
  TABLE_CONFIG_RESET,
  TABLE_TYPE_CHANGE
} from 'cadenza/result-table/toolbar/table-settings/event-types';

import i18n from './table-settings.properties';
import './table-settings.css';

const NAME = 'd-table-settings';

const ATTRIBUTE_TYPE_DIMENSION = 'DIMENSION';
const ATTRIBUTE_TYPE_MEASURE = 'MEASURE';
const PIVOT_CONFIG_TYPE_ROW = 'ROW';
const PIVOT_CONFIG_TYPE_COLUMN = 'COL';
const PIVOT_CONFIG_TYPE_MEASURE = 'MEASURE';
const TABLE_TYPE_PIVOT = 'PIVOT';
const TABLE_TYPE_OLAP = 'OLAP';

class TableSettings extends HTMLElement {

  #olapTableSettings;
  #pivotTableSettings;

  connectedCallback () {
    this.classList.add(NAME);
    this._fullContentSpinner = document.querySelector('.full-content-spinner');
  }

  initTableSettings (toolbar, tableController) {
    this._showSidebarBtn = h('button.button.button-icon.button-borderless', {
      type: 'button',
      id: 'column-configuration',
      title: i18n('button.title')
    }, icon(btnIcon));

    toolbar.append(this._showSidebarBtn);

    this._tableController = this._initTableController(tableController);

    this._eventKeys = [
      on(this._showSidebarBtn, [ 'click', 'touchstart' ], (event) => this._onShowSidebarBtnClicked(event))
    ];
  }

  disconnectedCallback () {
    unByKey(this._eventKeys);
    this._resetResultTableEventKeys();
    this._resetTableSettings();
    resetTableController(this._tableController);
  }

  _initTableController (tableController) {
    if (tableController) {
      tableController.setTableSettings(this);
    }
    return tableController;
  }

  _resetResultTableEventKeys () {
    if (this._resultTableEventKeys && this._resultTableEventKeys.length > 0) {
      unByKey(this._resultTableEventKeys);
    }
    this._resultTableEventKeys = [];
  }

  _resetTableSettings () {
    this._resetOlapTableSettings();
    this._resetPivotTableSettings();
  }

  _resetPivotTableSettings () {
    this.#pivotTableSettings = {
      cols: [],
      rows: [],
      measures: []
    };
  }

  _resetOlapTableSettings () {
    this.#olapTableSettings = {
      tableColumnElements: []
    };
  }

  _resetOlapConfiguration (tableColumnNamesWithVisibleIndicator) {
    this._olapColumnList.innerHTML = '';
    let visibilityMap;
    if (tableColumnNamesWithVisibleIndicator) {
      visibilityMap = tableColumnNamesWithVisibleIndicator.reduce((accumulator, element) => ({
        ...accumulator,
        [element.name]: element.visible
      }), {});
    }
    this._createOlapMenuStructureOnResultTable(visibilityMap);
  }

  get _resultTable () {
    return document.querySelector('.d-table');
  }

  _onShowSidebarBtnClicked () {
    if (!this._sidebar) {
      this._sidebar = this._initializeSidebar();
      this._resetTableSettings();
      this._initConfigLists(this._sidebar);
      this._createOlapMenuStructureOnResultTable();
      this._initTableType(this._sidebar, false);
      this._toggleAddButtonsState();
    }
    this._showSidebarBtn.hidden = true;
    this._sidebar.open = true;
    this._sidebar.hidden = false;

    toggleNarrowResultTable(true);

    if (!this._resultTable) {
      this._resetResultTableEventKeys();
      return;
    }

    if (this._resultTableEventKeys.length === 0) {
      this._setResultTableEventKeys();
    }
  }

  _initializeSidebar () {
    const sidebar = document.querySelector('.table-settings-menu');
    this._olapTableContent = sidebar.querySelector('.olap-table-content');
    this._pivotTableContent = sidebar.querySelector('.pivot-table-content');

    this._virtualValuesAggregation = createPivotVirtualValuesAggregation();
    this._virtualValuesColumnEntry = createPivotVirtualValuesColumnEntry(this._virtualValuesAggregation);

    this._addOlapTableColumnsBtn = sidebar.querySelector('.olap-table-columns-configuration');
    this._addRowBtn = sidebar.querySelector('.rows-configuration');
    this._addColumnBtn = sidebar.querySelector('.columns-configuration');
    this._addMeasureBtn = sidebar.querySelector('.measures-configuration');

    setupMenuButton(this._addOlapTableColumnsBtn,
      () => this._createAddOlapTableColumnMenuContent(),
      { styleClass: 'table-settings-menu',
        onClose: () => inactivateButton(this._addOlapTableColumnsBtn) });
    setupMenuButton(this._addRowBtn,
      () => this._createAddPivotColumnMenuContent(ATTRIBUTE_TYPE_DIMENSION, PIVOT_CONFIG_TYPE_ROW),
      { styleClass: 'table-settings-menu',
        onClose: () => inactivateButton(this._addRowBtn) });
    setupMenuButton(this._addColumnBtn,
      () => this._createAddPivotColumnMenuContent(ATTRIBUTE_TYPE_DIMENSION, PIVOT_CONFIG_TYPE_COLUMN),
      { styleClass: 'table-settings-menu',
        onClose: () => inactivateButton(this._addColumnBtn) });
    setupMenuButton(this._addMeasureBtn,
      () => this._createAddPivotColumnMenuContent(ATTRIBUTE_TYPE_MEASURE, PIVOT_CONFIG_TYPE_MEASURE),
      { styleClass: 'table-settings-menu',
        onClose: () => inactivateButton(this._addMeasureBtn) });

    const tableTypeSelector = sidebar.querySelector('.table-type-select');
    if (tableTypeSelector) {
      this._eventKeys.push(
        on(tableTypeSelector, 'change', event => this._onTableTypeChange(event.target.value, true))
      );
    }
    this._eventKeys.push(
      on(sidebar, 'close', () => this._onSidebarClosed()),
      on(sidebar.querySelector('.d-sidebar-reset'), 'click', () => this.#onResetBtnClicked()),
      on(this._resultTable, PIVOT_CHANGE, (event) => this._onExternalPivotChange(event.detail)),
      on(this._resultTable, COLUMN_VISIBILITY_CHANGE, (event) => this._onExternalColumnVisibilityChange(event.detail))
    );
    this._resultTableEventKeys = [];
    return sidebar;
  }

  #onResetBtnClicked () {
    this._tableController.tableSettingsReset();
    this._resultTableEventKeys.push(on(this._resultTable, AFTER_LOAD_DATA, () => {
      this._resetTableSettings();
      this._initTableType(this._sidebar, false);
      this._resetOlapConfiguration();
      this._notifyOlapConfigChange(TABLE_CONFIG_RESET);
    }, { once: true }));
  }

  _onExternalPivotChange ({ pivotTableConfig }) {
    const nameAggregation = createNameAggregation(this._resultTable);
    this.#pivotTableSettings.rows = [];
    pivotTableConfig.rows.forEach(aggName => {
      this.#pivotTableSettings.rows.push(nameAggregation[aggName]);
    });

    this._initPivotTableSettingsRowsSection();
    this._checkAlertMessages();
    this._toggleAddButtonsState();
  }

  _onExternalColumnVisibilityChange ({ tableColumnNamesWithVisibleIndicator }) {
    this._resetOlapConfiguration(tableColumnNamesWithVisibleIndicator);
  }

  _initTableType (sidebar, triggerReload) {
    this._tableType = sidebar.querySelector('.table-type-select');
    if (!this._tableType) {
      return;
    }
    if (this._hasPivotConfiguration() || hasPivotCustomConfig()) {
      this._tableType.querySelector(`option[value="${TABLE_TYPE_PIVOT}"]`).selected = true;
      this._onTableTypeChange(TABLE_TYPE_PIVOT, triggerReload);
    } else {
      this._tableType.querySelector(`option[value="${TABLE_TYPE_OLAP}"]`).selected = true;
      this._onTableTypeChange(TABLE_TYPE_OLAP, triggerReload);
    }
  }

  _hasPivotConfiguration () {
    return this._resultTable && isValidPivotConfiguration(this._resultTable.pivotConfiguration.pivotTableConfig, this._resultTable.pivotConfiguration);
  }

  _initConfigLists (sidebar) {
    this._olapColumnList = sidebar.querySelector('.olap-table-columns-section .tableColumns');
    this._enableReordering(this._olapColumnList);

    this._dimensionsRowList = sidebar.querySelector('.rows-section .tableColumns');
    this._enableReordering(this._dimensionsRowList, PIVOT_CONFIG_TYPE_ROW);

    this._dimensionsColumnList = sidebar.querySelector('.columns-section .tableColumns');
    this._enableReordering(this._dimensionsColumnList, PIVOT_CONFIG_TYPE_COLUMN);

    this._measuresList = sidebar.querySelector('.measures-section .tableColumns');
    this._enableReordering(this._measuresList, PIVOT_CONFIG_TYPE_MEASURE);
  }

  _initPivotTableSettingsSections () {
    this._initPivotTableSettingsRowsSection();
    this._initPivotTableSettingsColumnsSection();
    this._initPivotTableSettingsMeasuresSection();
    this._checkAlertMessages();
  }

  _initPivotTableSettingsRowsSection () {
    this._dimensionsRowList.innerHTML = '';
    this.#pivotTableSettings.rows.forEach(aggregation => {
      this._dimensionsRowList.append(this._createPivotTableColumnElement(aggregation, PIVOT_CONFIG_TYPE_ROW));
    });
  }

  _initPivotTableSettingsColumnsSection () {
    this._dimensionsColumnList.innerHTML = '';
    this.#pivotTableSettings.cols.forEach(aggregation => {
      const menuEntry = aggregation === this._virtualValuesAggregation ? this._virtualValuesColumnEntry : this._createPivotTableColumnElement(aggregation, PIVOT_CONFIG_TYPE_COLUMN);
      this._dimensionsColumnList.append(menuEntry);
    });
  }

  _initPivotTableSettingsMeasuresSection () {
    this._measuresList.innerHTML = '';
    this.#pivotTableSettings.measures.forEach(aggregation => {
      this._measuresList.append(this._createPivotTableColumnElement(aggregation, PIVOT_CONFIG_TYPE_MEASURE));
    });
  }

  _checkAlertMessages () {
    const pivotTableConfig = this._getPivotTableConfig();
    this._dimensionsRowList.nextElementSibling.classList.toggle('hidden', isRowsCountMinValid(pivotTableConfig, this._resultTable.pivotConfiguration));
    this._dimensionsColumnList.nextElementSibling.classList.toggle('hidden', isColsCountMinValid(pivotTableConfig, this._resultTable.pivotConfiguration));
    this._measuresList.nextElementSibling.classList.toggle('hidden', isMeasuresCountMinValid(pivotTableConfig, this._resultTable.pivotConfiguration));

    const visibleTableColumns = this._menuTableColumns.querySelectorAll('.tableColumn');
    const noColumnVisible = visibleTableColumns.length === 0;
    this._olapColumnList.nextElementSibling.classList.toggle('hidden', !noColumnVisible);
  }

  _setResultTableEventKeys () {
    this._resetResultTableEventKeys();
    if (this._resultTable) {
      this._resultTableEventKeys.push(on(this._resultTable, AFTER_LOAD_DATA, () => this._hideProgressSpinner()));
    }
  }

  _onSidebarClosed () {
    this._showSidebarBtn.hidden = false;
    toggleNarrowResultTable(false);
  }

  _chooseAddBtn (pivotType) {
    if (pivotType === PIVOT_CONFIG_TYPE_COLUMN) {
      return this._addColumnBtn;
    }
    if (pivotType === PIVOT_CONFIG_TYPE_ROW) {
      return  this._addRowBtn;
    }
    return this._addMeasureBtn;
  }

  _createAddPivotColumnMenuContent (attributeType, pivotType) {
    activateButton(this._chooseAddBtn(pivotType));
    const menuContent = [];
    const aggregatedColumnsOfType = mapAggregatedColumns(aggregateHierarchyColumns(this._resultTable))[attributeType];
    aggregatedColumnsOfType.filter(aggregation => canBeAdded(aggregation)).forEach(aggregation => {
      if (!this._isAggregationAlreadyContainedInPivotConfiguration(aggregation, pivotType) && this._doesAggregationFitInConfiguration(aggregation, pivotType)) {
        const menuEntry = this._createAddPivotMenuItem(aggregation, pivotType);
        menuContent.push(menuEntry);
      }
    });
    return menuContent;
  }

  _createAddOlapTableColumnMenuContent () {
    activateButton(this._addOlapTableColumnsBtn);
    const menuContent = [];
    const aggregations = aggregateHierarchyColumns(this._resultTable);
    aggregations.filter(aggregation => canBeAdded(aggregation)).forEach(aggregation => {
      if (!this._isAggregationAlreadyContainedInOlapTableConfiguration(aggregation)) {
        const menuItem = this._createAddOlapMenuItem(aggregation);
        menuContent.push(menuItem);
      }
    });
    return menuContent;
  }

  _isAggregationAlreadyContainedInOlapTableConfiguration (aggregation) {
    const visibleTableColumns = [ ...this._menuTableColumns.querySelectorAll('.tableColumn') ];
    const filterFunction = (columnEntry) => {
      return (isHierarchy(columnEntry.columns) && isHierarchy(aggregation) && columnEntry.columns[0].name === aggregation[0].name)
      || (!isHierarchy(columnEntry.columns) && !isHierarchy(aggregation) && columnEntry.column.name === aggregation.name);
    };
    return visibleTableColumns.filter(filterFunction).length > 0;
  }

  _createAddOlapMenuItem (aggregation) {
    const olapMenuItemExecute = () => {
      const olapTableColumnElement = this._createOlapTableColumnElement(aggregation, true);
      this._addOlapTableColumnElement(olapTableColumnElement);
    };
    return createAddMenuItem(aggregation, olapMenuItemExecute);
  }

  _addOlapTableColumnElement (tableColumnElement) {
    this._olapColumnList.append(tableColumnElement);
    const tableColumnElementIndexInOlapTableSettings = this.#olapTableSettings.tableColumnElements
      .findIndex((invisibleTableColumnElement) => isTableColumnElementEqual(tableColumnElement, invisibleTableColumnElement));
    applyVisibility(this.#olapTableSettings.tableColumnElements[tableColumnElementIndexInOlapTableSettings], true);
    const moveToIndexInOlapColumnList = this._olapColumnList.childElementCount - 1;
    this._moveOlapTableColumnElement(tableColumnElement, moveToIndexInOlapColumnList);
    this._checkAlertMessages();
    this._notifyOlapConfigChange(COLUMN_VISIBILITY_CHANGE);
  }

  _getAggregationNames (aggregatedColumns) {
    return aggregatedColumns.map(aggregation => getName(aggregation));
  }

  _createOlapMenuStructureOnResultTable (visibilityMap) {
    const sidebar = document.querySelector('.table-settings-menu');
    const menuTableColumns = sidebar.querySelector('ol.tableColumns');
    const aggregatedColumns = aggregateHierarchyColumns(this._resultTable);
    this.#olapTableSettings.tableColumnElements = [];
    aggregatedColumns.forEach(aggregation => {
      const columnElement = this._createOlapTableColumnElementFromVisibilityMap(aggregation, visibilityMap);
      const isColumnElementVisible = isHierarchy(aggregation) && columnElement.columns[0].visible
        || !isHierarchy(aggregation) && columnElement.column.visible;
      if (isColumnElementVisible) {
        menuTableColumns.append(columnElement);
      }
      this.#olapTableSettings.tableColumnElements.push(columnElement);
    });
    this._menuTableColumns = menuTableColumns;
    this._checkAlertMessages();
  }

  _isAggregationAlreadyContainedInPivotConfiguration (aggregation, pivotType) {
    const filterFunction = agg =>
      (isHierarchy(agg) && isHierarchy(aggregation) && agg[0].name === aggregation[0].name)
      || (!isHierarchy(agg) && !isHierarchy(aggregation) && agg.name === aggregation.name);
    if (pivotType === PIVOT_CONFIG_TYPE_ROW) {
      return this.#pivotTableSettings.rows.filter(filterFunction).length > 0;
    } else if (pivotType === PIVOT_CONFIG_TYPE_COLUMN) {
      return this.#pivotTableSettings.cols.filter(filterFunction).length > 0;
    } else {
      return this.#pivotTableSettings.measures.filter(filterFunction).length > 0;
    }
  }

  _doesAggregationFitInConfiguration (aggregation, pivotType) {
    let currentConfigArray, maxAggregationsAllowed;
    if (pivotType === PIVOT_CONFIG_TYPE_ROW) {
      currentConfigArray = this.#pivotTableSettings.rows;
      maxAggregationsAllowed = this._resultTable.pivotConfiguration.maxCountDimensionsRows;
    } else if (pivotType === PIVOT_CONFIG_TYPE_COLUMN) {
      currentConfigArray = this.#pivotTableSettings.cols;
      maxAggregationsAllowed = this._resultTable.pivotConfiguration.maxCountDimensionsColumns;
    } else {
      currentConfigArray = this.#pivotTableSettings.measures;
      maxAggregationsAllowed = this._resultTable.pivotConfiguration.maxCountMeasures;
    }

    const availableSpace = maxAggregationsAllowed - getAggregationsSize(currentConfigArray, this._resultTable.pivotConfiguration.virtualValuesDimension);
    return getAggregationSize(aggregation) <= availableSpace;
  }

  _createAddPivotMenuItem (aggregation, pivotConfigType) {
    const pivotMenuItemExecute = () => {
      const pivotTableColumnElement = this._createPivotTableColumnElement(aggregation, pivotConfigType);
      if (pivotConfigType === PIVOT_CONFIG_TYPE_ROW) {
        this.#pivotTableSettings.cols = removeAggregationFromConfigAndHtml(this.#pivotTableSettings.cols, this._dimensionsColumnList, aggregation);
        this._togglePivotAddButtonState(PIVOT_CONFIG_TYPE_COLUMN);
      } else if (pivotConfigType === PIVOT_CONFIG_TYPE_COLUMN) {
        this.#pivotTableSettings.rows = removeAggregationFromConfigAndHtml(this.#pivotTableSettings.rows, this._dimensionsRowList, aggregation);
        this._togglePivotAddButtonState(PIVOT_CONFIG_TYPE_ROW);
      }
      this._addPivotTableColumnElement(pivotConfigType, aggregation, pivotTableColumnElement);
    };
    return createAddMenuItem(aggregation, pivotMenuItemExecute);
  }

  _addPivotTableColumnElement (pivotConfigType, aggregation, menuEntry) {
    if (pivotConfigType === PIVOT_CONFIG_TYPE_ROW) {
      addConfigToBackingLists(this.#pivotTableSettings.rows, this._dimensionsRowList, aggregation, menuEntry);
    } else if (pivotConfigType === PIVOT_CONFIG_TYPE_COLUMN) {
      addConfigToBackingLists(this.#pivotTableSettings.cols, this._dimensionsColumnList, aggregation, menuEntry);
    } else if (pivotConfigType === PIVOT_CONFIG_TYPE_MEASURE) {
      addConfigToBackingLists(this.#pivotTableSettings.measures, this._measuresList, aggregation, menuEntry);
      if ([ ...this._measuresList.children ].length === 2) {
        addConfigToBackingLists(this.#pivotTableSettings.cols, this._dimensionsColumnList, this._virtualValuesAggregation, this._virtualValuesColumnEntry, true);
      }
    }
    this._togglePivotAddButtonState(pivotConfigType);
    this._checkAlertMessages();
    this._notifyPivotConfigChange();
  }

  _toggleAddButtonsState () {
    this._toggleOlapAddButtonState();
    this._togglePivotAddButtonState(PIVOT_CONFIG_TYPE_ROW);
    this._togglePivotAddButtonState(PIVOT_CONFIG_TYPE_COLUMN);
    this._togglePivotAddButtonState(PIVOT_CONFIG_TYPE_MEASURE);
  }

  _toggleOlapAddButtonState () {
    this._addOlapTableColumnsBtn.disabled = !this.#olapTableSettings.tableColumnElements.some(tableColumnElement => isVisibleTableColumnElement(tableColumnElement));
  }

  _togglePivotAddButtonState (pivotConfigType) {
    const { [ATTRIBUTE_TYPE_DIMENSION]: allConfigurableDimensions, [ATTRIBUTE_TYPE_MEASURE]: allConfigurableMeasures } = mapAggregatedColumns(aggregateHierarchyColumns(this._resultTable));

    if (pivotConfigType === PIVOT_CONFIG_TYPE_MEASURE) {
      this._addMeasureBtn.disabled = !this._isAnyAggregationSelectable(
        this.#pivotTableSettings.measures,
        PIVOT_CONFIG_TYPE_MEASURE,
        allConfigurableMeasures,
        this._resultTable.pivotConfiguration?.maxCountMeasures);
    } else if (pivotConfigType !== PIVOT_CONFIG_TYPE_MEASURE) {
      this._addRowBtn.disabled = !this._isAnyAggregationSelectable(
        this.#pivotTableSettings.rows,
        PIVOT_CONFIG_TYPE_ROW,
        allConfigurableDimensions,
        this._resultTable.pivotConfiguration?.maxCountDimensionsColumns);
      this._addColumnBtn.disabled = !this._isAnyAggregationSelectable(this.#pivotTableSettings.cols, PIVOT_CONFIG_TYPE_COLUMN, allConfigurableDimensions, this._resultTable.pivotConfiguration?.maxCountDimensionsColumns);
    }
  }

  _isAnyAggregationSelectable (selectedAggregations, pivotConfigType, allAggregationsOfType, maxAggregationsAllowed) {
    const aggregationsSpaceLeft = maxAggregationsAllowed - getAggregationsSize(selectedAggregations, this._resultTable ? this._resultTable.pivotConfiguration?.virtualValuesDimension : '');
    if (aggregationsSpaceLeft > 0) {
      const remainingAggregations = this._getRemainingAggregations(allAggregationsOfType, pivotConfigType);
      return remainingAggregations.some(remainingAggregation => getAggregationSize(remainingAggregation) <= aggregationsSpaceLeft);
    }
    return false;
  }

  _getRemainingAggregations (allAggregationsOfType, pivotConfigType) {
    const remainingAggregations = [];
    for (const aggregation of allAggregationsOfType) {
      if (!this._isAggregationAlreadyContainedInPivotConfiguration(aggregation, pivotConfigType)) {
        remainingAggregations.push(aggregation);
      }
    }
    return remainingAggregations;
  }

  _notifyPivotConfigChange () {
    const pivotTableConfig = this._getPivotTableConfig();
    this._notify(PIVOT_CHANGE, { pivotTableConfig });
  }

  _notifyTableTypeChange () {
    const isPivot = this._isPivotTableType();
    const pivotTableConfig = this._getPivotTableConfig();
    this._notify(TABLE_TYPE_CHANGE, { isPivot, pivotTableConfig });
  }

  _getPivotTableConfig () {
    const cols = [];
    const rows = [];
    const measures = [];
    this.#pivotTableSettings.cols.forEach(aggregation => cols.push(getName(aggregation)));
    this.#pivotTableSettings.rows.forEach(aggregation => rows.push(getName(aggregation)));
    this.#pivotTableSettings.measures.forEach(aggregation => measures.push(getName(aggregation)));
    const pivotTableConfig = {
      cols,
      rows,
      measures
    };
    const previousPivotValue = this._resultTable.pivotConfiguration?.pivotTableConfig;
    if (previousPivotValue && previousPivotValue.sortDescriptions) {
      pivotTableConfig.sortDescriptions = previousPivotValue.sortDescriptions;
    }
    return pivotTableConfig;
  }

  _onTableTypeChange (currentValue, shouldNotifyTableTypeChange) {
    const isPivot = currentValue === TABLE_TYPE_PIVOT;
    this._pivotTableContent.hidden = !isPivot;
    this._olapTableContent.hidden = isPivot;
    if (isPivot) {
      this._initializePivotConfiguration();
      if (shouldNotifyTableTypeChange) {
        this._notifyTableTypeChange();
      }
    } else {
      let columns = [];
      columns = columns.concat(this.#pivotTableSettings.rows);
      columns = columns.concat(this.#pivotTableSettings.cols.filter(col => Array.isArray(col) || col.name !== this._virtualValuesAggregation.name));
      columns = columns.concat(this.#pivotTableSettings.measures);
      this._resetPivotTableSettings();
      // special handling for switching a pivot table to a olap table,
      // url parameter has to be added and table will be refreshed completely
      if (shouldNotifyTableTypeChange) {
        this._initializeOlapConfiguration(columns);
        this._notifyOlapConfigChange(COLUMN_ORDER_CHANGE);
      }
    }
  }

  _initializePivotConfiguration () {
    // recreating virtual menu entry - workaround for IE CADENZA-11744
    this._virtualValuesColumnEntry = createPivotVirtualValuesColumnEntry(this._virtualValuesAggregation);
    const tablePivotConfiguration = this._resultTable.pivotConfiguration;
    if (this._hasPivotConfiguration()) {
      const nameAggregation = createNameAggregation(this._resultTable);
      tablePivotConfiguration.pivotTableConfig.cols.forEach(aggName => {
        if (aggName === this._virtualValuesAggregation.name) {
          this.#pivotTableSettings.cols.push(this._virtualValuesAggregation);
        } else {
          this.#pivotTableSettings.cols.push(nameAggregation[aggName]);
        }
      });
      tablePivotConfiguration.pivotTableConfig.rows.forEach(aggName => {
        this.#pivotTableSettings.rows.push(nameAggregation[aggName]);
      });
      tablePivotConfiguration.pivotTableConfig.measures.forEach(aggName => {
        this.#pivotTableSettings.measures.push(nameAggregation[aggName]);
      });
    } else {
      const { [ATTRIBUTE_TYPE_DIMENSION]: allConfigurableDimensions, [ATTRIBUTE_TYPE_MEASURE]: allConfigurableMeasures } = mapAggregatedColumns(aggregateHierarchyColumns(this._resultTable));

      const indexToSkip = getIndexToSkipForRowAssigningToHaveOneValueForColumns(allConfigurableDimensions, tablePivotConfiguration.initialCountDimensionsColumns);
      const rows = getPossibleValuesFitting({
        possibleValues: allConfigurableDimensions,
        wantedSize: tablePivotConfiguration.initialCountDimensionsRows,
        maxSize: tablePivotConfiguration.maxCountDimensionsRows,
        skipIndex: indexToSkip
      });
      this.#pivotTableSettings.rows.push(...rows);

      const possibleCols = allConfigurableDimensions.filter(dimension => !rows.includes(dimension));
      const cols = getPossibleValuesFitting({
        possibleValues: possibleCols,
        wantedSize: tablePivotConfiguration.initialCountDimensionsColumns,
        maxSize: tablePivotConfiguration.maxCountDimensionsColumns
      });
      this.#pivotTableSettings.cols.push(...cols);

      const measures = getPossibleValuesFitting({
        possibleValues: allConfigurableMeasures,
        wantedSize: tablePivotConfiguration.initialCountMeasures,
        maxSize: tablePivotConfiguration.maxCountMeasures
      });
      this.#pivotTableSettings.measures.push(...measures);

      if (measures.length > 1) {
        this.#pivotTableSettings.cols.unshift(this._virtualValuesAggregation);
      }
    }

    this._initPivotTableSettingsSections();
    this._toggleAddButtonsState();
  }

  _initializeOlapConfiguration (columns) {
    this._olapColumnList.innerHTML = '';
    const sidebar = document.querySelector('.table-settings-menu');
    const menuTableColumns = sidebar.querySelector('ol.tableColumns');
    const aggregatedColumns = aggregateHierarchyColumns(this._resultTable);
    const aggregationNames = this._getAggregationNames(aggregatedColumns);
    const columnNames = this._getAggregationNames(columns);
    const invisibleAggregationNames = aggregationNames.filter(name => !columnNames.includes(name));
    this.#olapTableSettings.tableColumnElements = [];
    aggregatedColumns.forEach(aggregatedColumn => {
      const name = getName(aggregatedColumn);
      const isVisible = !invisibleAggregationNames.includes(name);
      const olapTableColumnElement = this._createOlapTableColumnElement(aggregatedColumn, isVisible);
      if (isVisible) {
        menuTableColumns.append(olapTableColumnElement);
      }
      this.#olapTableSettings.tableColumnElements.push(olapTableColumnElement);
    });
    this._menuTableColumns = menuTableColumns;
    this._checkAlertMessages();
  }

  _showProgressSpinner () {
    this._togglePivotSpinnerVisibility(true);
  }

  _hideProgressSpinner () {
    this._togglePivotSpinnerVisibility(false);
  }

  _togglePivotSpinnerVisibility (visibile) {
    // (when used in dashboard, the spinner is not available by selector)
    if (this._fullContentSpinner) {
      this._fullContentSpinner.hidden = !visibile;
    }
  }

  _createOlapTableColumnElementFromVisibilityMap (aggregation, visibilityMap) {
    if (isHierarchy(aggregation)) {
      const isAnyLevelInHierarchyVisible = aggregation.some(c => c.visible);
      const firstColumnInHierarchy = aggregation[0];
      const isHierarchyMadeInvisible = visibilityMap && !visibilityMap[firstColumnInHierarchy.hierarchyName];
      const isAggregationVisible = isAnyLevelInHierarchyVisible && !isHierarchyMadeInvisible;
      const hierarchyElement = this._createOlapTableColumnElement(aggregation, isAggregationVisible);
      hierarchyElement.shouldBeVisibleInHierarchyPanel = this._resultTable.pivotConfiguration?.pivotTableConfig?.cols?.includes(firstColumnInHierarchy.hierarchyName);
      return hierarchyElement;
    }
    const isAggregationVisible = aggregation.visible && !(visibilityMap && !visibilityMap[aggregation.name]);
    return this._createOlapTableColumnElement(aggregation, isAggregationVisible);
  }

  _createOlapTableColumnElement (aggregation, isVisible) {
    const onOlapMenuItemDelete = (event) => this._removeOlapTableColumnElement(event);
    return createTableColumnElement(aggregation, isVisible, onOlapMenuItemDelete);
  }

  _removeOlapTableColumnElement (event) {
    const tableColumnElement = event.target.closest('.tableColumn');
    const indexOfRemovedTableColumnElement = this.#olapTableSettings.tableColumnElements
      .findIndex((invisibleTableColumnElement) => isTableColumnElementEqual(tableColumnElement, invisibleTableColumnElement));
    applyVisibility(this.#olapTableSettings.tableColumnElements[indexOfRemovedTableColumnElement], false);
    tableColumnElement.remove();
    this._checkAlertMessages();
    this._notifyOlapConfigChange(COLUMN_VISIBILITY_CHANGE);
  }

  _createPivotTableColumnElement (aggregation, pivotConfigType) {
    const onPivotMenuItemDelete = () => this._removePivotTableColumnElement(pivotConfigType, aggregation);
    return createTableColumnElement(aggregation, true, onPivotMenuItemDelete);
  }

  _removePivotTableColumnElement (pivotConfigType, aggregation) {
    if (pivotConfigType === PIVOT_CONFIG_TYPE_ROW) {
      this.#pivotTableSettings.rows = removeAggregationFromConfigAndHtml(this.#pivotTableSettings.rows, this._dimensionsRowList, aggregation);
    } else if (pivotConfigType === PIVOT_CONFIG_TYPE_COLUMN) {
      this.#pivotTableSettings.cols = removeAggregationFromConfigAndHtml(this.#pivotTableSettings.cols, this._dimensionsColumnList, aggregation);
    } else if (pivotConfigType === PIVOT_CONFIG_TYPE_MEASURE) {
      this.#pivotTableSettings.measures = removeAggregationFromConfigAndHtml(this.#pivotTableSettings.measures, this._measuresList, aggregation);
      const measuresList = [ ...this._measuresList.children ];
      if (measuresList.length === 1) {
        const virtualAggregationIndex = this.#pivotTableSettings.cols.findIndex(c => c.name === this._virtualValuesAggregation.name);
        this.#pivotTableSettings.cols.splice(virtualAggregationIndex, 1);
        this._dimensionsColumnList.removeChild(this._virtualValuesColumnEntry);
      }
    }
    this._togglePivotAddButtonState(pivotConfigType);
    this._checkAlertMessages();
    this._notifyPivotConfigChange();
  }

  _enableReordering (menuTableColumns, pivotConfigType) {
    const options = {
      onUpdate: (event) => {
        if (pivotConfigType) {
          this._movePivotItemInList(pivotConfigType, event.oldIndex, event.newIndex);
          this._notifyPivotConfigChange();
        } else {
          this._moveOlapTableColumnElement(event.item, event.newIndex);
          this._notifyOlapConfigChange(COLUMN_ORDER_CHANGE);
        }
      }
    };
    if (supportsTouch) {
      options.handle = '.drag-indicator';
    }
    Sortable.create(menuTableColumns, options);
  }

  _movePivotItemInList (pivotConfigType, fromIndex, toIndex) {
    if (fromIndex !== toIndex) {
      if (pivotConfigType === PIVOT_CONFIG_TYPE_ROW) {
        this.#pivotTableSettings.rows.splice(toIndex, 0, this.#pivotTableSettings.rows.splice(fromIndex, 1)[0]);
      } else if (pivotConfigType === PIVOT_CONFIG_TYPE_COLUMN) {
        this.#pivotTableSettings.cols.splice(toIndex, 0, this.#pivotTableSettings.cols.splice(fromIndex, 1)[0]);
      } else {
        this.#pivotTableSettings.measures.splice(toIndex, 0, this.#pivotTableSettings.measures.splice(fromIndex, 1)[0]);
      }
    }
  }

  _moveOlapTableColumnElement (item, toIndex) {
    let visibleTableColumnElementCounter = 0;
    const indexToMoveTo = this.#olapTableSettings.tableColumnElements.findIndex(tableColumnElement => {
      if (isVisibleTableColumnElement(tableColumnElement)) {
        if (visibleTableColumnElementCounter === toIndex) {
          return true;
        }
        visibleTableColumnElementCounter += 1;
      }
      return false;
    });
    const movedTableColumnIndex = this.#olapTableSettings.tableColumnElements.findIndex(tableColumnElement => isTableColumnElementEqual(tableColumnElement, item));
    this.#olapTableSettings.tableColumnElements.splice(indexToMoveTo, 0, this.#olapTableSettings.tableColumnElements.splice(movedTableColumnIndex, 1)[0]);
  }

  _isPivotTableType () {
    return this._tableType?.value === TABLE_TYPE_PIVOT;
  }

  _notifyOlapConfigChange (customEventName) {
    const tableColumnNamesWithVisibleIndicator = [];
    this.#olapTableSettings.tableColumnElements
      .forEach(invisibleTableColumnElement =>  tableColumnNamesWithVisibleIndicator.push(createOlapTableColumnNameWithVisibleIndicator(invisibleTableColumnElement)));
    this._notify(customEventName, { tableColumnNamesWithVisibleIndicator });
  }

  _notify (eventType, data) {
    if (this._isPivotTableType() && isValidPivotConfiguration(this._getPivotTableConfig(), this._resultTable.pivotConfiguration)) {
      this._showProgressSpinner();
    } else if (!this._isPivotTableType()) {
      this._showProgressSpinner();
    }
    this.dispatchEvent(new CustomEvent(eventType, { detail: data, bubbles: true }));
  }

}

customElements.define(NAME, TableSettings);

function resetTableController (tableController) {
  if (tableController) {
    tableController.resetTableSettings();
    tableController = null;
  }
}

function getTitle (aggregation) {
  return !isHierarchy(aggregation) ? aggregation.title : hierarchyTitle(aggregation);
}

function getName (aggregation) {
  return isHierarchy(aggregation) ? aggregation[0].hierarchyName : aggregation.name;
}

function isHierarchy (aggregation) {
  return Array.isArray(aggregation) && aggregation.length > 0 && !!aggregation[0].hierarchyName;
}

function isTableColumnElementEqual (tableColumnElementA, tableColumnElementB) {
  const isEqualHierarchy = isHierarchy(tableColumnElementA.columns)
    && isHierarchy(tableColumnElementB.columns)
    && tableColumnElementA.hierarchyName === tableColumnElementB.hierarchyName
    && hierarchyTitle(tableColumnElementA.columns) === hierarchyTitle(tableColumnElementB.columns);
  const isEqualDimension = !isHierarchy(tableColumnElementA.columns)
    && !isHierarchy(tableColumnElementB.columns)
    && tableColumnElementA.column
    && tableColumnElementB.column
    && tableColumnElementA.column.name === tableColumnElementB.column.name;
  return isEqualHierarchy || isEqualDimension;
}

function hierarchyTitle (columns) {
  if (columns.length === 0) {
    return '';
  }
  const hierarchyName = columns[0].hierarchyPrintName;
  const levelPrintNames = columns.map(c => c.title).join(', ');
  return `${hierarchyName} (${levelPrintNames})`;
}

function getDescription (aggregation) {
  return !isHierarchy(aggregation) ? aggregation.description : i18n('hierarchy.description');
}

function createNameAggregation (resultTable) {
  const aggregatedColumns = aggregateHierarchyColumns(resultTable);
  const nameAggregation = {};
  aggregatedColumns.forEach(aggregation => {
    nameAggregation[getName(aggregation)] = aggregation;
  });
  return nameAggregation;
}

function aggregateHierarchyColumns (resultTable) {
  const result = [];
  let currentHierarchyColumns = [];
  const isHierarchy = c => !!c.hierarchyName;
  if (resultTable) {
    for (const column of resultTable.configurationColumns) {
      if (isHierarchy(column)) {
        const lastIndex = currentHierarchyColumns.length - 1;
        if (lastIndex > -1
          && column.hierarchyName !== currentHierarchyColumns[lastIndex].hierarchyName) {
          result.push(currentHierarchyColumns);
          currentHierarchyColumns = [];
        }
        currentHierarchyColumns.push(column);
      } else {
        if (currentHierarchyColumns.length > 0) {
          result.push(currentHierarchyColumns);
          currentHierarchyColumns = [];
        }
        result.push(column);
      }
    }
  }
  if (currentHierarchyColumns.length > 0) {
    result.push(currentHierarchyColumns);
  }
  return result;
}

function getAggregationsSize (aggregations, virtualValuesDimensionName) {
  return aggregations.reduce((acc, aggregation) =>
    aggregation.name === virtualValuesDimensionName ? acc : acc + getAggregationSize(aggregation), 0);
}

function getAggregationSize (aggregation) {
  return isHierarchy(aggregation) ? aggregation.length : 1;
}

function isVisibleAggregation (aggregation) {
  return _isVisibleHierarchy(aggregation) || _isVisibleDimension(aggregation);
}

function _isVisibleHierarchy (aggregation) {
  if (!aggregation || !isHierarchy(aggregation)) {
    return false;
  }
  return aggregation[0].visible;
}

function _isVisibleDimension (aggregation) {
  if (!aggregation || isHierarchy(aggregation)) {
    return false;
  }
  return aggregation.visible;
}

function isVisibleTableColumnElement (tableColumnElement) {
  return isVisibleAggregation(tableColumnElement.columns)
    || isVisibleAggregation(tableColumnElement.column);
}

function applyVisibility (tableColumnElement, visibility) {
  if (isHierarchy(tableColumnElement.columns)) {
    tableColumnElement.columns[0].visible = visibility;
  } else {
    tableColumnElement.column.visible = visibility;
  }
}

function toggleNarrowResultTable (toggle) {
  const tableResultsTable = document.querySelector('.table-results-table');
  if (tableResultsTable) {
    tableResultsTable.classList.toggle('narrow-table-results-table', toggle);
  }
}

function createOlapTableColumnNameWithVisibleIndicator (tableColumnElement) {
  if (isHierarchy(tableColumnElement.columns)) {
    return {
      name: tableColumnElement.hierarchyName,
      visible: tableColumnElement.columns[0].visible || tableColumnElement.shouldBeVisibleInHierarchyPanel
    };
  }
  return {
    name: tableColumnElement.column.name,
    visible: tableColumnElement.column.visible
  };
}

function createTableColumnElement (aggregation, isVisible, onDelete) {
  const title = getTitle(aggregation);
  const description = getDescription(aggregation);

  const deleteButton = h('button.button.button-icon.button-borderless', {
    type: 'button',
    onclick: (event) => onDelete(event)
  }, icon(clearRoundIcon));
  const tableColumnElement = h('li.tableColumn', {}, [
    h('span.columnActions',
      h('span.drag-indicator', icon(dragIcon))),
    h('span.columnText', [ h('span.columnTitle', title), h('small.columnDescription', description) ])
  ]);

  let availableForProjection;
  if (isHierarchy(aggregation)) {
    tableColumnElement.columns = aggregation;
    tableColumnElement.hierarchyName = aggregation[0].hierarchyName;
    availableForProjection = aggregation.some(el => el.availableForProjection);
  } else {
    tableColumnElement.column = aggregation;
    availableForProjection = aggregation.availableForProjection;
  }
  if (availableForProjection) {
    tableColumnElement.append(deleteButton);
  }
  applyVisibility(tableColumnElement, isVisible);
  return tableColumnElement;
}

function createAddMenuItem (aggregation, menuItemExecute) {
  return {
    label: h('div.tableColumn',
      h('span.columnText', [
        h('span.columnTitle', getTitle(aggregation)),
        h('small.columnDescription', getDescription(aggregation))
      ])),
    execute: menuItemExecute
  };
}

function addConfigToBackingLists (configList, htmlList, aggregation, htmlEntry, addToFront) {
  if (addToFront) {
    configList.unshift(aggregation);
    htmlList.prepend(htmlEntry);
  } else {
    configList.push(aggregation);
    htmlList.append(htmlEntry);
  }
}

function removeAggregationFromConfigAndHtml (configList, htmlList, aggregation) {
  const name = getName(aggregation);

  const filteredList = configList.filter((value) => !nameMatchesAggregation(name, value));

  [ ...htmlList.children ].forEach(menuEntry => {
    if ((menuEntry.column && menuEntry.column.name === name) || (menuEntry.columns && menuEntry.columns[0].hierarchyName === name)) {
      htmlList.removeChild(menuEntry);
    }
  });

  return filteredList;
}

function nameMatchesAggregation (name, aggregation) {
  return getName(aggregation) === name;
}

function createPivotVirtualValuesAggregation () {
  return { name: VIRTUAL_VALUES_DIMENSION, title: i18n('virtualValuesColumnLabel') };
}

function createPivotVirtualValuesColumnEntry (virtualValuesAggregation) {
  const menuEntry = h('li.tableColumn', {}, [
    h('span.columnActions', h('span.drag-indicator', icon(dragIcon))),
    h('span.columnText', [ h('span.columnTitle', virtualValuesAggregation.title) ])
  ]);
  menuEntry.column = virtualValuesAggregation;
  return menuEntry;
}

function activateButton (button) {
  button.classList.add('active');
}

function inactivateButton (button) {
  button.classList.remove('active');
}

function mapAggregatedColumns (aggregations) {
  const dimensions = [];
  const measures = [];
  for (const aggregation of aggregations) {
    if (isHierarchy(aggregation) || aggregation.olapAttributeDefinitionType === ATTRIBUTE_TYPE_DIMENSION) {
      dimensions.push(aggregation);
    } else if (!isHierarchy(aggregation)) {
      measures.push(aggregation);
    }
  }
  return { [ATTRIBUTE_TYPE_DIMENSION]: dimensions, [ATTRIBUTE_TYPE_MEASURE]: measures };
}

function getIndexToSkipForRowAssigningToHaveOneValueForColumns (possibleValues, max) {
  const visibleIndices = [];
  let lastFittingVisibleIndex = -1;
  for (let i = possibleValues.length - 1; i >= 0; i--) {
    if (lastFittingVisibleIndex === -1
      && isVisibleAggregation(possibleValues[i])
      && getAggregationSize(possibleValues[i]) <= max) {
      lastFittingVisibleIndex = i;
    } else if (isVisibleAggregation(possibleValues[i])) {
      visibleIndices.push(i);
    }
  }
  // If there is only one visible possible value this shouldn't be used as column but as row
  // so it is the same as this value would not be found
  if (visibleIndices.length === 0 && lastFittingVisibleIndex !== -1) {
    return -1;
  }
  return lastFittingVisibleIndex;
}

function getPossibleValuesFitting ({ possibleValues, wantedSize, maxSize, skipIndex = -1 }) {
  const ret = [];
  let maxAllowedSize = maxSize;
  for (let i = 0; i < possibleValues.length; i++) {
    if (wantedSize > 0 && i !== skipIndex && isVisibleAggregation(possibleValues[i])) {
      const currentAggregationSize = getAggregationSize(possibleValues[i]);
      if (currentAggregationSize <= maxAllowedSize) {
        ret.push(possibleValues[i]);
        wantedSize -= currentAggregationSize;
        maxAllowedSize -= currentAggregationSize;
      }
    }
    if (wantedSize <= 0) {
      break;
    }
  }
  return ret;
}

function hasPivotCustomConfig () {
  return hasParameter(PARAMETER_PIVOT_CONFIG);
}

function canBeAdded (aggregation) {
  if (Array.isArray(aggregation)) {
    return aggregation.filter(aggr => aggr.availableForProjection).length > 0;
  }
  return aggregation.availableForProjection;
}
